export {
  receiverSecret,
  receiverChallenge,
  startCenterLogin,
  startHostedCenterLogin,
  centerHandoff,
} from "./center";
export interface AuthClientConfiguration {
  readonly apiOrigin: URL;
}

export type IdentityProvider = "google" | "apple" | "wechat" | "center";

export interface ProviderAuthTransaction {
  readonly authorizationUrl: URL;
  readonly state: string;
  readonly nonce: string;
  readonly pkceVerifier: string;
  readonly expiresAt: string;
}

export interface ProviderCallbackInput {
  readonly state: string;
  readonly nonce: string;
  readonly pkceVerifier: string;
  readonly code: string;
}

export interface ProviderCallbackResult {
  readonly transactionId: string;
  readonly exchangeCode?: string;
  readonly newAccount: boolean;
  readonly completed: boolean;
}

export function createAuthClientConfiguration(
  apiOrigin: URL,
): AuthClientConfiguration {
  return Object.freeze({ apiOrigin: new URL(apiOrigin) });
}

export interface SessionTokens {
  readonly accessToken: string;
  readonly accessExpiresAt: string;
  readonly refreshToken: string;
  readonly sessionGeneration: number;
  readonly absoluteExpiresAt: string;
}

export interface StoredSession {
  readonly refreshToken: string;
  readonly sessionGeneration: number;
  readonly accountGeneration: number;
  readonly absoluteExpiresAt: string;
}

export interface SessionStorage {
  load(): Promise<StoredSession | undefined>;
  save(session: StoredSession): Promise<void>;
  clear(): Promise<void>;
}

export interface DeviceRegistration {
  readonly exchangeCode: string;
  readonly receiverVerifier?: string;
  readonly requireFreshAuthentication?: boolean;
  readonly displayName: string;
  readonly platform: string;
  readonly clientVersion: string;
}

export interface SessionTransport {
  exchange(registration: DeviceRegistration): Promise<SessionTokens>;
  refresh(refreshToken: string): Promise<SessionTokens>;
  logout(refreshToken: string): Promise<void>;
}

export interface SessionState extends SessionTokens {
  readonly accountGeneration: number;
}

export class AuthClientError extends Error {
  constructor(
    readonly code:
      "AUTH_REQUIRED" | "STALE_SESSION_RESULT" | "SESSION_PERSISTENCE_FAILED",
  ) {
    super(code);
  }
}

export class ApiResponseError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

export class HttpIdentityTransport {
  readonly #configuration: AuthClientConfiguration;
  readonly #fetch: typeof fetch;

  constructor(
    configuration: AuthClientConfiguration,
    fetchImplementation: typeof fetch = globalThis.fetch,
  ) {
    this.#configuration = configuration;
    this.#fetch = fetchImplementation.bind(globalThis);
  }

  async providers(): Promise<readonly IdentityProvider[]> {
    const response = await this.#fetch(
      new URL("/v1/auth/providers", this.#configuration.apiOrigin),
      { method: "GET", cache: "no-store" },
    );
    if (!response.ok) throw await responseError(response);
    const value: unknown = await response.json();
    const providers = parseEnabledProviders(value);
    if (!providers)
      throw new ApiResponseError("INVALID_RESPONSE", response.status);
    return providers;
  }

  async start(provider: IdentityProvider): Promise<ProviderAuthTransaction> {
    const response = await this.#fetch(
      new URL(`/v1/auth/${provider}/start`, this.#configuration.apiOrigin),
      { method: "POST", cache: "no-store" },
    );
    if (!response.ok) throw await responseError(response);
    const value: unknown = await response.json();
    const transaction = parseProviderAuthTransaction(value);
    if (!transaction)
      throw new ApiResponseError("INVALID_RESPONSE", response.status);
    return transaction;
  }

  async callback(
    provider: IdentityProvider,
    input: ProviderCallbackInput,
  ): Promise<ProviderCallbackResult> {
    const response = await this.#fetch(
      new URL(`/v1/auth/${provider}/callback`, this.#configuration.apiOrigin),
      requestInit({
        state: input.state,
        nonce: input.nonce,
        pkce_verifier: input.pkceVerifier,
        code: input.code,
      }),
    );
    if (!response.ok) throw await responseError(response);
    const value: unknown = await response.json();
    const result = parseProviderCallbackResult(value);
    if (!result)
      throw new ApiResponseError("INVALID_RESPONSE", response.status);
    return result;
  }
}

export class HttpSessionTransport implements SessionTransport {
  readonly #configuration: AuthClientConfiguration;
  readonly #fetch: typeof fetch;

  constructor(
    configuration: AuthClientConfiguration,
    fetchImplementation: typeof fetch = globalThis.fetch,
  ) {
    this.#configuration = configuration;
    this.#fetch = fetchImplementation.bind(globalThis);
  }

  exchange(registration: DeviceRegistration): Promise<SessionTokens> {
    return this.#request("/v1/sessions/exchange", {
      exchange_code: registration.exchangeCode,
      ...(registration.receiverVerifier
        ? { receiver_verifier: registration.receiverVerifier }
        : {}),
      ...(registration.requireFreshAuthentication === true
        ? { require_fresh_auth: true }
        : {}),
      display_name: registration.displayName,
      platform: registration.platform,
      client_version: registration.clientVersion,
    });
  }

  refresh(refreshToken: string): Promise<SessionTokens> {
    return this.#request("/v1/sessions/refresh", {
      refresh_token: refreshToken,
    });
  }

  async logout(refreshToken: string): Promise<void> {
    const response = await this.#fetch(
      new URL("/v1/sessions/logout", this.#configuration.apiOrigin),
      requestInit({ refresh_token: refreshToken }),
    );
    if (!response.ok) throw await responseError(response);
  }

  async #request(path: string, body: object): Promise<SessionTokens> {
    const response = await this.#fetch(
      new URL(path, this.#configuration.apiOrigin),
      requestInit(body),
    );
    if (!response.ok) throw await responseError(response);
    const value: unknown = await response.json();
    const tokens = parseSessionTokenResponse(value);
    if (!tokens)
      throw new ApiResponseError("INVALID_RESPONSE", response.status);
    return tokens;
  }
}

const terminalSessionCodes = new Set([
  "AUTH_REQUIRED",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
  "SESSION_REUSE_DETECTED",
]);

export function isTerminalSessionError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string" &&
    terminalSessionCodes.has(error.code)
  );
}

function invalidatesLocalSession(error: unknown): boolean {
  return (
    isTerminalSessionError(error) ||
    (error instanceof ApiResponseError && error.code === "INVALID_RESPONSE")
  );
}

export class SessionController {
  readonly #storage: SessionStorage;
  readonly #transport: SessionTransport;
  #state: SessionState | undefined;
  #pendingState: SessionState | undefined;
  #accountGeneration = 0;
  #persistence: Promise<void> = Promise.resolve();
  #storageNeedsClear = false;

  constructor(storage: SessionStorage, transport: SessionTransport) {
    this.#storage = storage;
    this.#transport = transport;
  }

  current(): Readonly<SessionState> | undefined {
    return this.#state;
  }

  async establish(tokens: SessionTokens): Promise<Readonly<SessionState>> {
    const accountGeneration = this.#accountGeneration + 1;
    this.#accountGeneration = accountGeneration;
    this.#state = undefined;
    return this.#persist(Object.freeze({ ...tokens, accountGeneration }));
  }

  async exchange(
    registration: DeviceRegistration,
  ): Promise<Readonly<SessionState>> {
    const accountGeneration = this.#accountGeneration + 1;
    this.#accountGeneration = accountGeneration;
    this.#state = undefined;
    await this.#queuePersistence(() => this.#clearStorage());

    const tokens = await this.#transport.exchange(registration);
    if (this.#accountGeneration !== accountGeneration)
      throw new AuthClientError("STALE_SESSION_RESULT");

    return this.#persist(Object.freeze({ ...tokens, accountGeneration }));
  }

  async restore(): Promise<Readonly<SessionState> | undefined> {
    // Storage may resolve after a new login or logout. Do not let that read
    // acquire a newer generation, refresh old credentials, or clear new state.
    const readGeneration = this.#accountGeneration;
    if (this.#storageNeedsClear)
      await this.#queuePersistence(() => this.#clearStorage());
    const stored = await this.#storage.load();
    if (readGeneration !== this.#accountGeneration)
      throw new AuthClientError("STALE_SESSION_RESULT");
    if (
      !isStoredSession(stored) ||
      Date.parse(stored.absoluteExpiresAt) <= Date.now()
    ) {
      if (stored !== undefined)
        await this.#queuePersistence(() => this.#clearStorage());
      return undefined;
    }
    const accountGeneration = Math.max(
      this.#accountGeneration + 1,
      stored.accountGeneration,
    );
    this.#accountGeneration = accountGeneration;
    try {
      const tokens = await this.#transport.refresh(stored.refreshToken);
      if (tokens.sessionGeneration <= stored.sessionGeneration)
        throw new ApiResponseError("INVALID_RESPONSE", 200);
      if (this.#accountGeneration !== accountGeneration)
        throw new AuthClientError("STALE_SESSION_RESULT");
      return await this.#persist(
        Object.freeze({ ...tokens, accountGeneration }),
      );
    } catch (error) {
      if (
        this.#accountGeneration === accountGeneration &&
        invalidatesLocalSession(error)
      ) {
        await this.#invalidate(accountGeneration);
      }
      throw error;
    }
  }

  async refresh(): Promise<Readonly<SessionState>> {
    const captured = this.#requiredState();
    try {
      const tokens = await this.#transport.refresh(captured.refreshToken);
      if (tokens.sessionGeneration <= captured.sessionGeneration)
        throw new ApiResponseError("INVALID_RESPONSE", 200);
      if (!this.#isCurrent(captured.accountGeneration))
        throw new AuthClientError("STALE_SESSION_RESULT");
      return await this.#persist(
        Object.freeze({
          ...tokens,
          accountGeneration: captured.accountGeneration,
        }),
      );
    } catch (error) {
      if (invalidatesLocalSession(error))
        await this.#invalidate(captured.accountGeneration);
      throw error;
    }
  }

  async authorized<T>(
    operation: (accessToken: string) => Promise<T>,
  ): Promise<T> {
    const captured = this.#requiredState();
    try {
      const result = await operation(captured.accessToken);
      if (!this.#isCurrent(captured.accountGeneration))
        throw new AuthClientError("STALE_SESSION_RESULT");
      return result;
    } catch (error) {
      if (isTerminalSessionError(error)) {
        // A request can finish after the same account has rotated its access
        // token. Its rejection says nothing about the newer credential.
        const latest =
          this.#pendingState?.accountGeneration === this.#accountGeneration
            ? this.#pendingState
            : this.#state;
        if (
          latest &&
          (latest.accountGeneration !== captured.accountGeneration ||
            latest.accessToken !== captured.accessToken ||
            latest.sessionGeneration !== captured.sessionGeneration)
        )
          throw new AuthClientError("STALE_SESSION_RESULT");
        // With no replacement credential, sibling cancellation after genuine
        // revocation remains AUTH_REQUIRED rather than a recoverable stale read.
        await this.#invalidate(captured.accountGeneration);
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    const captured = this.#state;
    this.#state = undefined;
    this.#accountGeneration += 1;
    const clear = this.#queuePersistence(() => this.#clearStorage());
    const revoke = captured
      ? this.#transport.logout(captured.refreshToken)
      : Promise.resolve();
    const [clearResult, revokeResult] = await Promise.allSettled([
      clear,
      revoke,
    ]);
    if (revokeResult.status === "rejected") throw revokeResult.reason;
    if (clearResult.status === "rejected") throw clearResult.reason;
  }

  #requiredState(): SessionState {
    if (!this.#state) throw new AuthClientError("AUTH_REQUIRED");
    return this.#state;
  }

  #isCurrent(accountGeneration: number): boolean {
    return this.#state?.accountGeneration === accountGeneration;
  }

  async #invalidate(accountGeneration: number): Promise<void> {
    if (this.#accountGeneration !== accountGeneration) return;
    this.#state = undefined;
    this.#accountGeneration += 1;
    await this.#queuePersistence(() => this.#clearStorage());
  }

  async #persist(state: SessionState): Promise<Readonly<SessionState>> {
    this.#pendingState = state;
    const stored: StoredSession = {
      refreshToken: state.refreshToken,
      sessionGeneration: state.sessionGeneration,
      accountGeneration: state.accountGeneration,
      absoluteExpiresAt: state.absoluteExpiresAt,
    };
    try {
      await this.#queuePersistence(async () => {
        if (this.#accountGeneration !== state.accountGeneration)
          throw new AuthClientError("STALE_SESSION_RESULT");
        try {
          if (this.#storageNeedsClear) await this.#clearStorage();
          await this.#storage.save(stored);
        } catch {
          if (this.#accountGeneration === state.accountGeneration) {
            this.#state = undefined;
            this.#accountGeneration += 1;
          }
          // A failed save may still have written. Keep cleanup in the same
          // persistence queue so it cannot erase a newer login's saved state.
          try {
            await this.#clearStorage();
          } catch {
            // A later restore must clear this uncertain write before reading it.
          }
          throw new AuthClientError("SESSION_PERSISTENCE_FAILED");
        }
        if (this.#accountGeneration !== state.accountGeneration)
          throw new AuthClientError("STALE_SESSION_RESULT");
        this.#state = state;
      });
      if (!this.#isCurrent(state.accountGeneration))
        throw new AuthClientError("STALE_SESSION_RESULT");
      return state;
    } finally {
      if (this.#pendingState === state) this.#pendingState = undefined;
    }
  }

  async #clearStorage(): Promise<void> {
    this.#storageNeedsClear = true;
    try {
      await this.#storage.clear();
      this.#storageNeedsClear = false;
    } catch {
      throw new AuthClientError("SESSION_PERSISTENCE_FAILED");
    }
  }

  #queuePersistence(operation: () => Promise<void>): Promise<void> {
    const result = this.#persistence.then(operation, operation);
    this.#persistence = result.catch(() => undefined);
    return result;
  }
}

function isStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<StoredSession>;
  return (
    typeof candidate.refreshToken === "string" &&
    candidate.refreshToken.length > 0 &&
    typeof candidate.sessionGeneration === "number" &&
    Number.isSafeInteger(candidate.sessionGeneration) &&
    candidate.sessionGeneration > 0 &&
    typeof candidate.accountGeneration === "number" &&
    Number.isSafeInteger(candidate.accountGeneration) &&
    candidate.accountGeneration > 0 &&
    typeof candidate.absoluteExpiresAt === "string" &&
    Number.isFinite(Date.parse(candidate.absoluteExpiresAt))
  );
}

function requestInit(body: object): RequestInit {
  return {
    method: "POST",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

const identityProviders = new Set<IdentityProvider>([
  "google",
  "apple",
  "wechat",
  "center",
]);

function parseEnabledProviders(
  value: unknown,
): readonly IdentityProvider[] | undefined {
  if (typeof value !== "object" || value === null || !("providers" in value))
    return undefined;
  const providers = (value as { providers?: unknown }).providers;
  if (!Array.isArray(providers)) return undefined;
  const result: IdentityProvider[] = [];
  for (const provider of providers) {
    if (
      typeof provider !== "string" ||
      !identityProviders.has(provider as IdentityProvider) ||
      result.includes(provider as IdentityProvider)
    ) {
      return undefined;
    }
    result.push(provider as IdentityProvider);
  }
  return Object.freeze(result);
}

function parseProviderAuthTransaction(
  value: unknown,
): ProviderAuthTransaction | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const response = value as Record<string, unknown>;
  if (
    typeof response.authorization_url !== "string" ||
    typeof response.state !== "string" ||
    typeof response.nonce !== "string" ||
    typeof response.pkce_verifier !== "string" ||
    typeof response.expires_at !== "string" ||
    response.state.length === 0 ||
    response.nonce.length === 0 ||
    response.pkce_verifier.length === 0 ||
    !Number.isFinite(Date.parse(response.expires_at))
  ) {
    return undefined;
  }
  let authorizationUrl: URL;
  try {
    authorizationUrl = new URL(response.authorization_url);
  } catch {
    return undefined;
  }
  if (authorizationUrl.protocol !== "https:") return undefined;
  return {
    authorizationUrl,
    state: response.state,
    nonce: response.nonce,
    pkceVerifier: response.pkce_verifier,
    expiresAt: response.expires_at,
  };
}

function parseProviderCallbackResult(
  value: unknown,
): ProviderCallbackResult | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const response = value as Record<string, unknown>;
  if (
    typeof response.transaction_id !== "string" ||
    typeof response.new_account !== "boolean" ||
    typeof response.completed !== "boolean" ||
    (response.exchange_code !== undefined &&
      (typeof response.exchange_code !== "string" ||
        response.exchange_code.length === 0))
  ) {
    return undefined;
  }
  const result: ProviderCallbackResult = {
    transactionId: response.transaction_id,
    newAccount: response.new_account,
    completed: response.completed,
  };
  if (typeof response.exchange_code === "string")
    return { ...result, exchangeCode: response.exchange_code };
  return result;
}

async function responseError(response: Response): Promise<ApiResponseError> {
  let code = "REQUEST_FAILED";
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "code" in body &&
      typeof body.code === "string"
    ) {
      code = body.code;
    }
  } catch {
    // Error bodies are optional and never included in the thrown value.
  }
  return new ApiResponseError(code, response.status);
}

function parseSessionTokenResponse(value: unknown): SessionTokens | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const response = value as Record<string, unknown>;
  if (
    typeof response.access_token !== "string" ||
    typeof response.access_expires_at !== "string" ||
    typeof response.refresh_token !== "string" ||
    typeof response.session_generation !== "number" ||
    typeof response.absolute_expires_at !== "string" ||
    response.access_token.length === 0 ||
    response.refresh_token.length === 0 ||
    !Number.isSafeInteger(response.session_generation) ||
    response.session_generation < 1 ||
    !Number.isFinite(Date.parse(response.access_expires_at)) ||
    !Number.isFinite(Date.parse(response.absolute_expires_at))
  ) {
    return undefined;
  }
  return {
    accessToken: response.access_token,
    accessExpiresAt: response.access_expires_at,
    refreshToken: response.refresh_token,
    sessionGeneration: response.session_generation,
    absoluteExpiresAt: response.absolute_expires_at,
  };
}
